import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { ChevronDown } from "lucide-react";
import "./navbar.css";
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaTwitter, FaWhatsapp } from "react-icons/fa";

const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  {
    to: "/about",
    label: "About Us",
    dropdown: [
      { to: "/about", label: "About Us" },
      { to: "/journey", label: "Our Journey" },
    ],
  },
  { to: "/career", label: "Career" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blogs", label: "Blogs" },
  { to: "/contact", label: "Contact Us" },
];

const SOCIAL_FLOAT = [
  { href: "https://www.facebook.com/theinovativesolutions/", icon: <FaFacebookF />, color: "#1877f2", bg: "rgba(24,119,242,0.12)" },
  { href: "https://www.instagram.com/the.innovative.solutions", icon: <FaInstagram />, color: "#e1306c", bg: "rgba(225,48,108,0.12)" },
  { href: "https://www.linkedin.com/in/the-innovative-solutions-18255222a/", icon: <FaLinkedinIn />, color: "#0077b5", bg: "rgba(0,119,181,0.12)" },
  { href: "https://x.com/theinnovatives3", icon: <FaTwitter />, color: "#1da1f2", bg: "rgba(29,161,242,0.12)" },
  { href: "https://wa.me/919371001138?text=Hello%21%20Team%20Innovative%20Solutions", icon: <FaWhatsapp />, color: "#25d366", bg: "rgba(37,211,102,0.12)" },
];

// ── Animation Variants ──────────────────────────────────────

const navbarVariants = {
  hidden: { y: -60, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.55, delay: 0.15, ease: [0.22, 1, 0.36, 1] } },
};

const floatBarVariants = {
  hidden: { x: -50, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.3 } },
};

const navItemVariants = {
  hidden: { y: -16, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: "easeOut" } },
};

const mobileMenuVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: "auto", opacity: 1, transition: { duration: 0.32, ease: "easeInOut" } },
  exit: { height: 0, opacity: 0, transition: { duration: 0.25, ease: "easeInOut" } },
};

const mobileLinkVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: (i) => ({ x: 0, opacity: 1, transition: { delay: i * 0.04, duration: 0.3 } }),
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const navRef = useRef(null);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (v) => setIsScrolled(v > 25));

  // Close mobile menu on outside click
  useEffect(() => {
    const fn = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setIsMobileMenuOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  return (
    <>
      {/* ═══ Floating Social Sidebar ═══ */}
      <motion.div
        className="position-fixed d-none d-lg-flex flex-column"
        variants={floatBarVariants}
        initial="hidden"
        animate="visible"
        style={{
          left: 0, top: "50%", transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
          boxShadow: "4px 0 24px rgba(26,80,137,0.12), 0 0 0 1px rgba(26,80,137,0.08)",
          borderTopRightRadius: "14px", borderBottomRightRadius: "14px",
          padding: "14px 11px", zIndex: 1000, gap: "14px",
        }}
      >
        {SOCIAL_FLOAT.map((s, i) => (
          <motion.a
            key={i} href={s.href} target="_blank" rel="noopener noreferrer"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 + i * 0.08, type: "spring", stiffness: 300 }}
            whileHover={{ x: 6, scale: 1.2, backgroundColor: s.bg }}
            whileTap={{ scale: 0.88 }}
            style={{
              color: "#243e7c", display: "flex", alignItems: "center",
              justifyContent: "center", width: "30px", height: "30px",
              borderRadius: "8px", fontSize: "13px", textDecoration: "none",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = s.color}
            onMouseLeave={(e) => e.currentTarget.style.color = "#243e7c"}
          >
            {s.icon}
          </motion.a>
        ))}
      </motion.div>

      {/* ═══ Main Navbar ═══ */}
      <motion.nav
        ref={navRef}
        variants={navbarVariants}
        initial="hidden"
        animate="visible"
        style={{
          position: "sticky", top: 0, zIndex: 1050,
          background: "#ffffff",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          boxShadow: isScrolled ? "0 8px 30px rgba(0,0,0,0.04)" : "none",
          transition: "box-shadow 0.3s ease",
        }}
      >
        <div className="container" style={{ padding: "2px 16px" }}>
          <div className="d-flex align-items-center w-100">

            {/* ── Logo ── */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: "spring", stiffness: 350, damping: 18 }}
              style={{ mixBlendMode: "multiply" }}
            >
              <NavLink to="/" style={{ textDecoration: "none", display: "block" }}>
                <img
                  src="/images/logo.jpg"
                  alt="The Innovative Solutions"
                  style={{ height: "62px", objectFit: "contain", display: "block", mixBlendMode: "multiply" }}
                />
              </NavLink>
            </motion.div>

            {/* ── Desktop Nav Links ── */}
            <motion.ul
              className="navbar-nav ms-auto mb-0 d-none d-lg-flex align-items-center flex-row"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              style={{ gap: "2px", listStyle: "none", margin: 0, padding: 0, flexDirection: "row" }}
            >
              {NAV_LINKS.map((link) => {
                const hasDropdown = !!link.dropdown;
                return (
                  <motion.li
                    key={link.to}
                    variants={navItemVariants}
                    style={{ position: "relative", listStyle: "none" }}
                    onHoverStart={() => { setHoveredLink(link.to); if (hasDropdown) setAboutDropdownOpen(true); }}
                    onHoverEnd={() => { setHoveredLink(null); if (hasDropdown) setAboutDropdownOpen(false); }}
                  >
                    {hasDropdown ? (
                      /* ── Dropdown trigger (no navigation on click, only hover) ── */
                      <span
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "3px",
                          padding: "6px 13px", borderRadius: "7px", fontWeight: 700,
                          fontSize: "12.5px", letterSpacing: "0.45px", textTransform: "uppercase",
                          color: "#2d3a52", cursor: "pointer", userSelect: "none",
                          transition: "color 0.2s ease",
                        }}
                      >
                        {link.label}
                        <motion.span
                          animate={{ rotate: aboutDropdownOpen ? 180 : 0 }}
                          transition={{ duration: 0.25 }}
                          style={{ display: "inline-flex", alignItems: "center", marginTop: "1px" }}
                        >
                          <ChevronDown size={13} strokeWidth={2.5} />
                        </motion.span>
                      </span>
                    ) : (
                      <NavLink
                        to={link.to}
                        end={link.end}
                        onClick={() => setIsMobileMenuOpen(false)}
                        style={({ isActive }) => ({
                          display: "inline-block", padding: "6px 13px",
                          borderRadius: "7px", fontWeight: 700, fontSize: "12.5px",
                          letterSpacing: "0.45px", textTransform: "uppercase",
                          color: isActive ? "#1a5089" : "#2d3a52",
                          textDecoration: "none",
                          background: "transparent",
                          transition: "color 0.2s ease",
                        })}
                      >
                        {link.label}
                      </NavLink>
                    )}

                    {/* Animated underline on hover */}
                    <AnimatePresence>
                      {hoveredLink === link.to && (
                        <motion.span
                          layoutId="nav-hover-bar"
                          initial={{ scaleX: 0, opacity: 0 }}
                          animate={{ scaleX: 1, opacity: 1 }}
                          exit={{ scaleX: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          style={{
                            position: "absolute", bottom: "2px",
                            left: "13px", right: "13px", height: "2.5px",
                            background: "linear-gradient(90deg,#1a5089,#3D90D7)",
                            borderRadius: "2px", transformOrigin: "center",
                            display: "block",
                          }}
                        />
                      )}
                    </AnimatePresence>

                    {/* ── Dropdown Menu ── */}
                    {hasDropdown && (
                      <AnimatePresence>
                        {aboutDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.96 }}
                            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                            style={{
                              position: "absolute", top: "calc(100% + 6px)", left: "50%",
                              transform: "translateX(-50%)",
                              background: "rgba(255,255,255,0.96)",
                              backdropFilter: "blur(18px)",
                              WebkitBackdropFilter: "blur(18px)",
                              border: "1px solid rgba(36,62,124,0.1)",
                              borderRadius: "14px",
                              boxShadow: "0 16px 40px rgba(36,62,124,0.14), 0 2px 8px rgba(36,62,124,0.06)",
                              padding: "8px",
                              minWidth: "170px",
                              zIndex: 2000,
                            }}
                          >
                            {/* Arrow tip */}
                            <div style={{
                              position: "absolute", top: "-6px", left: "50%", transform: "translateX(-50%)",
                              width: 0, height: 0,
                              borderLeft: "6px solid transparent",
                              borderRight: "6px solid transparent",
                              borderBottom: "6px solid rgba(255,255,255,0.96)",
                            }} />
                            {link.dropdown.map((sub, si) => (
                              <NavLink
                                key={si}
                                to={sub.to}
                                onClick={() => { setAboutDropdownOpen(false); setIsMobileMenuOpen(false); }}
                                style={({ isActive }) => ({
                                  display: "flex", alignItems: "center", gap: "8px",
                                  padding: "9px 14px", borderRadius: "9px",
                                  fontWeight: 700, fontSize: "12.5px",
                                  letterSpacing: "0.35px", textTransform: "uppercase",
                                  color: isActive ? "#1a5089" : "#2d3a52",
                                  background: isActive ? "rgba(26,80,137,0.08)" : "transparent",
                                  textDecoration: "none",
                                  transition: "background 0.2s, color 0.2s",
                                  whiteSpace: "nowrap",
                                })}
                                onMouseEnter={e => {
                                  e.currentTarget.style.background = "rgba(36,62,124,0.07)";
                                  e.currentTarget.style.color = "#1a5089";
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.background = "transparent";
                                  e.currentTarget.style.color = "#2d3a52";
                                }}
                              >
                                <span style={{
                                  width: "6px", height: "6px", borderRadius: "50%",
                                  background: "linear-gradient(135deg,#243e7c,#3D90D7)",
                                  flexShrink: 0,
                                }} />
                                {sub.label}
                              </NavLink>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    )}
                  </motion.li>
                );
              })}

            </motion.ul>

            {/* ── Mobile Hamburger ── */}
            <motion.button
              className="d-lg-none ms-auto"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              whileTap={{ scale: 0.85 }}
              style={{
                border: "none", background: "rgba(36,62,124,0.07)",
                borderRadius: "8px", padding: "8px", width: "38px", height: "38px",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {[
                  isMobileMenuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 },
                  isMobileMenuOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 },
                  isMobileMenuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 },
                ].map((anim, i) => (
                  <motion.span
                    key={i}
                    animate={anim}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    style={{ display: "block", width: "20px", height: "2px", background: "#243e7c", borderRadius: "2px" }}
                  />
                ))}
              </div>
            </motion.button>
          </div>

          {/* ── Mobile Dropdown Menu ── */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                variants={mobileMenuVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ overflow: "hidden" }}
              >
                <ul style={{
                  listStyle: "none", margin: 0, padding: "10px 0 8px",
                  display: "flex", flexDirection: "column", gap: "3px",
                }}>
                  {NAV_LINKS.map((link, i) => (
                    <motion.li key={link.to} custom={i} variants={mobileLinkVariants} initial="hidden" animate="visible">
                      {link.dropdown ? (
                        /* ── Mobile accordion for About Us ── */
                        <>
                          <div
                            onClick={() => setMobileAboutOpen(o => !o)}
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "9px 14px", borderRadius: "8px", cursor: "pointer",
                              fontWeight: 700, fontSize: "13px",
                              color: "#2d3a52", textTransform: "uppercase", letterSpacing: "0.35px",
                            }}
                          >
                            <span>{link.label}</span>
                            <motion.span animate={{ rotate: mobileAboutOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                              <ChevronDown size={15} strokeWidth={2.5} />
                            </motion.span>
                          </div>
                          <AnimatePresence>
                            {mobileAboutOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                style={{ overflow: "hidden", paddingLeft: "14px" }}
                              >
                                {link.dropdown.map((sub, si) => (
                                  <NavLink
                                    key={si}
                                    to={sub.to}
                                    onClick={() => { setIsMobileMenuOpen(false); setMobileAboutOpen(false); }}
                                    style={({ isActive }) => ({
                                      display: "flex", alignItems: "center", gap: "8px",
                                      padding: "8px 14px", borderRadius: "8px",
                                      fontWeight: 700, fontSize: "12.5px",
                                      color: isActive ? "#1a5089" : "#475569",
                                      background: "transparent",
                                      textDecoration: "none", textTransform: "uppercase", letterSpacing: "0.3px",
                                    })}
                                  >
                                    <span style={{
                                      width: "5px", height: "5px", borderRadius: "50%",
                                      background: "linear-gradient(135deg,#243e7c,#3D90D7)",
                                      flexShrink: 0,
                                    }} />
                                    {sub.label}
                                  </NavLink>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <NavLink
                          to={link.to} end={link.end}
                          onClick={() => setIsMobileMenuOpen(false)}
                          style={({ isActive }) => ({
                            display: "block", padding: "9px 14px", borderRadius: "8px",
                            fontWeight: 700, fontSize: "13px",
                            color: isActive ? "#1a5089" : "#2d3a52",
                            background: "transparent",
                            textDecoration: "none", textTransform: "uppercase", letterSpacing: "0.35px",
                          })}
                        >
                          {link.label}
                        </NavLink>
                      )}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>
    </>
  );
};

export default Navbar;
